import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { HOLIDAY_TOPICS } from "../lib/content";
import { BRAND } from "../lib/brand";
import MiniLeadForm from "../components/MiniLeadForm";

export default function HolidayHomes() {
  return (
    <div data-testid="holiday-homes-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">Holiday Homes</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 max-w-4xl leading-[1.1]">In Goa & Uttarakhand — the difference is in the research.</h1>
        <p className="text-lg text-zinc-600 mt-8 max-w-3xl leading-relaxed">
          A holiday home is not just a second property. When bought right, it generates rental income while appreciating in value. When bought wrong, it sits empty, costs money to maintain, and becomes a liability.
        </p>
        <p className="text-zinc-700 mt-4 max-w-3xl leading-relaxed">
          The difference between the two is entirely in the research before the purchase — not the brochure during the sales pitch.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-4">
        {BRAND.holidayImages.slice(0, 3).map((src, i) => (
          <div key={i} className={`rounded-3xl overflow-hidden border border-zinc-200 ${i === 0 ? "md:col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`}>
            <img src={src} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-3xl">What you need to understand before investing</h2>
        <p className="text-zinc-600 mt-4 max-w-2xl">Each of these topics has a dedicated page. Start with whichever is most relevant to you.</p>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {HOLIDAY_TOPICS.map((t) => (
            <Link key={t.slug} to={`/holiday-homes/${t.slug}`} className="group bg-white border border-zinc-200 rounded-2xl p-7 hover:border-[#1D4ED8] hover:-translate-y-1 transition-all" data-testid={`topic-${t.slug}`}>
              <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">{t.kicker}</div>
              <h3 className="font-display text-2xl mt-2 leading-snug">{t.title}</h3>
              <p className="text-zinc-600 mt-3 text-sm line-clamp-3">{t.summary}</p>
              <div className="mt-5 inline-flex items-center gap-1 text-[#1D4ED8] text-sm font-medium">Read <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12">
        <MiniLeadForm
          context="holiday"
          title="Want a personalised holiday-home plan?"
          sub="Share your name, email and phone — we'll call you within 24 hours with location options, realistic returns, and a clear next step. No spam. No broker chain."
        />
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1D4ED8] to-[#1E40AF] text-white p-10 md:p-16">
          <div aria-hidden className="absolute -right-20 -bottom-20 w-[320px] h-[320px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 bg-white/15 text-white px-3 py-1 rounded-full text-xs tracking-wide uppercase font-medium">Holiday Homes</span>
              <h3 className="font-display text-3xl sm:text-4xl mt-4 leading-tight">Still unsure where to start?</h3>
              <p className="text-white/85 mt-4 max-w-lg leading-relaxed">A 45-minute Clarity Call to help you understand which market, which budget, and which property type actually fits your goals.</p>
            </div>
            <Link to="/clarity-call" className="inline-flex items-center gap-2 bg-white text-[#1D4ED8] hover:bg-zinc-100 px-6 py-3 rounded-full font-medium">
              Book a Clarity Call
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
